/**
 * Login page brand themes.
 * Add a new entry and map hostnames / report keys / brand names to it.
 * Login.vue applies colors, welcome name, document title, and favicon.
 */

/** Used whenever a school theme has no favicon of its own */
export const DEFAULT_FAVICON = "/icon.png";

const DEFAULT_THEME = {
  id: "myskool",
  displayName: "mySkool",
  documentTitle: "MySkool App",
  favicon: DEFAULT_FAVICON,
  colors: {
    bg: "#f7f6fb",
    panel: "#ffffff",
    ink: "#1e1b2e",
    muted: "#6b6680",
    line: "rgba(108, 92, 231, 0.16)",
    accent: "#6c5ce7",
    accentSoft: "#a29bfe",
    accentDeep: "#5b4cc4",
    input: "#ffffff",
    shadow: "0 16px 40px rgba(108, 92, 231, 0.1)",
    accentRgb: "108, 92, 231",
    accentSoftRgb: "162, 155, 254",
    gradient: "linear-gradient(135deg, #6c5ce7 0%, #8f82f0 55%, #a29bfe 100%)",
    gradientHover: "linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%)",
  },
  dark: {
    bg: "#15121f",
    panel: "#1f1a2e",
    ink: "#f4f1ff",
    muted: "#b3acc8",
    line: "rgba(162, 155, 254, 0.22)",
    accent: "#a29bfe",
    accentSoft: "#6c5ce7",
    accentDeep: "#8c82eb",
    input: "#2a243b",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.35)",
    accentRgb: "162, 155, 254",
    accentSoftRgb: "108, 92, 231",
    gradient: "linear-gradient(135deg, #6c5ce7 0%, #8f82f0 55%, #a29bfe 100%)",
    gradientHover: "linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%)",
  },
};

/** HPGS / HPGS Connect — official logo red + charcoal */
const HPGS_RED = "#E31E24";
const HPGS_RED_DEEP = "#C4191E";
const HPGS_RED_SOFT = "#F04A4F";
const HPGS_BLACK = "#4A4A49";
const HPGS_INK = "#2F2F2E";

const HPGS_THEME = {
  id: "hpgs",
  displayName: "HPGS",
  documentTitle: "HPGS",
  favicon: "/hpgs-logo.png",
  colors: {
    bg: "#f7f7f7",
    panel: "#ffffff",
    ink: HPGS_INK,
    muted: "#6b6b6a",
    line: "rgba(227, 30, 36, 0.2)",
    accent: HPGS_RED,
    accentSoft: HPGS_RED_SOFT,
    accentDeep: HPGS_BLACK,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(227, 30, 36, 0.14)",
    accentRgb: "227, 30, 36",
    accentSoftRgb: "240, 74, 79",
    gradient: `linear-gradient(135deg, ${HPGS_BLACK} 0%, ${HPGS_RED_DEEP} 42%, ${HPGS_RED} 100%)`,
    gradientHover: `linear-gradient(135deg, #2F2F2E 0%, ${HPGS_RED_DEEP} 100%)`,
  },
  dark: {
    bg: "#141414",
    panel: "#1c1c1c",
    ink: "#f5f5f5",
    muted: "#b0b0af",
    line: "rgba(227, 30, 36, 0.35)",
    accent: HPGS_RED_SOFT,
    accentSoft: HPGS_RED,
    accentDeep: "#ff6b6f",
    input: "#2a2223",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "240, 74, 79",
    accentSoftRgb: "227, 30, 36",
    gradient: `linear-gradient(135deg, ${HPGS_BLACK} 0%, ${HPGS_RED_DEEP} 45%, ${HPGS_RED} 100%)`,
    gradientHover: `linear-gradient(135deg, #2F2F2E 0%, ${HPGS_RED_DEEP} 100%)`,
  },
};

/** IUSS / ERP IUSS — navy blue from IU School System logo */
const IUSS_BLUE = "#0B3D6E";
const IUSS_BLUE_DEEP = "#072A4D";
const IUSS_BLUE_SOFT = "#1A5F9E";
const IUSS_INK = "#0A2540";

const IUSS_THEME = {
  id: "iuss",
  displayName: "IUSS",
  documentTitle: "IUSS ERP",
  favicon: "/iuss-logo.png",
  colors: {
    bg: "#f5f8fb",
    panel: "#ffffff",
    ink: IUSS_INK,
    muted: "#5a6b7a",
    line: "rgba(11, 61, 110, 0.18)",
    accent: IUSS_BLUE,
    accentSoft: IUSS_BLUE_SOFT,
    accentDeep: IUSS_BLUE_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(11, 61, 110, 0.14)",
    accentRgb: "11, 61, 110",
    accentSoftRgb: "26, 95, 158",
    gradient: `linear-gradient(135deg, ${IUSS_BLUE_DEEP} 0%, ${IUSS_BLUE} 55%, ${IUSS_BLUE_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #051e38 0%, ${IUSS_BLUE_DEEP} 100%)`,
  },
  dark: {
    bg: "#0c1520",
    panel: "#152232",
    ink: "#eef4fa",
    muted: "#9bb0c4",
    line: "rgba(26, 95, 158, 0.35)",
    accent: IUSS_BLUE_SOFT,
    accentSoft: IUSS_BLUE,
    accentDeep: "#4d8ec4",
    input: "#1a2a3a",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "26, 95, 158",
    accentSoftRgb: "11, 61, 110",
    gradient: `linear-gradient(135deg, ${IUSS_BLUE_DEEP} 0%, ${IUSS_BLUE} 50%, ${IUSS_BLUE_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #051e38 0%, ${IUSS_BLUE} 100%)`,
  },
};

/** MAK Collegiate — official portal logo (login + splash + favicon) */
export const MAK_LOGO_URL =
  "https://cdn.cloudious.net/file-1786188205237-919305213.jpg";

/** MAK Collegiate — star logo red */
const MAK_RED = "#C62828";
const MAK_RED_DEEP = "#A31F1F";
const MAK_RED_SOFT = "#E53935";
const MAK_INK = "#1F1F1F";

const MAK_THEME = {
  id: "mak",
  displayName: "MAK Collegiate",
  documentTitle: "MAK Collegiate",
  favicon: MAK_LOGO_URL,
  logo: MAK_LOGO_URL,
  colors: {
    bg: "#faf7f7",
    panel: "#ffffff",
    ink: MAK_INK,
    muted: "#666666",
    line: "rgba(198, 40, 40, 0.2)",
    accent: MAK_RED,
    accentSoft: MAK_RED_SOFT,
    accentDeep: MAK_RED_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(198, 40, 40, 0.14)",
    accentRgb: "198, 40, 40",
    accentSoftRgb: "229, 57, 53",
    gradient: `linear-gradient(135deg, ${MAK_RED_DEEP} 0%, ${MAK_RED} 55%, ${MAK_RED_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #7A1717 0%, ${MAK_RED_DEEP} 100%)`,
  },
  dark: {
    bg: "#161212",
    panel: "#221c1c",
    ink: "#f5f5f5",
    muted: "#b8a8a8",
    line: "rgba(229, 57, 53, 0.35)",
    accent: MAK_RED_SOFT,
    accentSoft: MAK_RED,
    accentDeep: "#ff6b6b",
    input: "#2a2222",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "229, 57, 53",
    accentSoftRgb: "198, 40, 40",
    gradient: `linear-gradient(135deg, ${MAK_RED_DEEP} 0%, ${MAK_RED} 50%, ${MAK_RED_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #7A1717 0%, ${MAK_RED} 100%)`,
  },
};

/** Crest View Academy — navy crest */
const CREST_NAVY = "#0B2C5E";
const CREST_NAVY_DEEP = "#071E42";
const CREST_NAVY_SOFT = "#1A4A8A";
const CREST_INK = "#0A1F3D";

const CRESTVIEW_THEME = {
  id: "crestview",
  displayName: "Crest View",
  documentTitle: "Crest View Academy",
  favicon: null, // fall back to mySkool
  colors: {
    bg: "#f5f7fb",
    panel: "#ffffff",
    ink: CREST_INK,
    muted: "#5a6b7d",
    line: "rgba(11, 44, 94, 0.18)",
    accent: CREST_NAVY,
    accentSoft: CREST_NAVY_SOFT,
    accentDeep: CREST_NAVY_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(11, 44, 94, 0.14)",
    accentRgb: "11, 44, 94",
    accentSoftRgb: "26, 74, 138",
    gradient: `linear-gradient(135deg, ${CREST_NAVY_DEEP} 0%, ${CREST_NAVY} 55%, ${CREST_NAVY_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #041530 0%, ${CREST_NAVY_DEEP} 100%)`,
  },
  dark: {
    bg: "#0c1420",
    panel: "#152032",
    ink: "#eef3fa",
    muted: "#9bb0c4",
    line: "rgba(26, 74, 138, 0.35)",
    accent: CREST_NAVY_SOFT,
    accentSoft: CREST_NAVY,
    accentDeep: "#4d7ab8",
    input: "#1a2a3a",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "26, 74, 138",
    accentSoftRgb: "11, 44, 94",
    gradient: `linear-gradient(135deg, ${CREST_NAVY_DEEP} 0%, ${CREST_NAVY} 50%, ${CREST_NAVY_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #041530 0%, ${CREST_NAVY} 100%)`,
  },
};

/** The Elixir School — logo lime green */
const ELIXIR_GREEN = "#8CC63F";
const ELIXIR_GREEN_DEEP = "#6B9A2E";
const ELIXIR_GREEN_SOFT = "#A8D85C";
const ELIXIR_INK = "#2A2A2A";

const ELIXIR_THEME = {
  id: "elixir",
  displayName: "Elixir",
  documentTitle: "The Elixir School",
  favicon: null, // fall back to mySkool
  colors: {
    bg: "#f7faf4",
    panel: "#ffffff",
    ink: ELIXIR_INK,
    muted: "#666b60",
    line: "rgba(140, 198, 63, 0.22)",
    accent: ELIXIR_GREEN,
    accentSoft: ELIXIR_GREEN_SOFT,
    accentDeep: ELIXIR_GREEN_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(140, 198, 63, 0.16)",
    accentRgb: "140, 198, 63",
    accentSoftRgb: "168, 216, 92",
    gradient: `linear-gradient(135deg, ${ELIXIR_GREEN_DEEP} 0%, ${ELIXIR_GREEN} 55%, ${ELIXIR_GREEN_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #557A24 0%, ${ELIXIR_GREEN_DEEP} 100%)`,
  },
  dark: {
    bg: "#121610",
    panel: "#1c2218",
    ink: "#f5f5f5",
    muted: "#a8b8a0",
    line: "rgba(168, 216, 92, 0.35)",
    accent: ELIXIR_GREEN_SOFT,
    accentSoft: ELIXIR_GREEN,
    accentDeep: "#c5eb7a",
    input: "#222a1e",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "168, 216, 92",
    accentSoftRgb: "140, 198, 63",
    gradient: `linear-gradient(135deg, ${ELIXIR_GREEN_DEEP} 0%, ${ELIXIR_GREEN} 50%, ${ELIXIR_GREEN_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #557A24 0%, ${ELIXIR_GREEN} 100%)`,
  },
};

/** Profectus International — navy + gold */
const PROFECTUS_NAVY = "#0A2540";
const PROFECTUS_NAVY_DEEP = "#061828";
const PROFECTUS_GOLD = "#C9A227";
const PROFECTUS_INK = "#0A1E32";

const PROFECTUS_THEME = {
  id: "profectus",
  displayName: "Profectus",
  documentTitle: "Profectus International",
  favicon: null, // fall back to mySkool
  colors: {
    bg: "#f6f7f9",
    panel: "#ffffff",
    ink: PROFECTUS_INK,
    muted: "#5a6570",
    line: "rgba(10, 37, 64, 0.18)",
    accent: PROFECTUS_NAVY,
    accentSoft: "#1A4A70",
    accentDeep: PROFECTUS_NAVY_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(10, 37, 64, 0.14)",
    accentRgb: "10, 37, 64",
    accentSoftRgb: "26, 74, 112",
    gradient: `linear-gradient(135deg, ${PROFECTUS_NAVY_DEEP} 0%, ${PROFECTUS_NAVY} 50%, ${PROFECTUS_GOLD} 100%)`,
    gradientHover: `linear-gradient(135deg, #040e18 0%, ${PROFECTUS_NAVY_DEEP} 100%)`,
  },
  dark: {
    bg: "#0c1420",
    panel: "#152032",
    ink: "#eef3fa",
    muted: "#9bb0c4",
    line: "rgba(201, 162, 39, 0.35)",
    accent: PROFECTUS_GOLD,
    accentSoft: "#1A4A70",
    accentDeep: "#E0BC4A",
    input: "#1a2a3a",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "201, 162, 39",
    accentSoftRgb: "26, 74, 112",
    gradient: `linear-gradient(135deg, ${PROFECTUS_NAVY_DEEP} 0%, ${PROFECTUS_NAVY} 45%, ${PROFECTUS_GOLD} 100%)`,
    gradientHover: `linear-gradient(135deg, #040e18 0%, ${PROFECTUS_NAVY} 100%)`,
  },
};

/** SHS Schooling System — maroon shield */
const SHS_MAROON = "#6B1E2A";
const SHS_MAROON_DEEP = "#4A1420";
const SHS_MAROON_SOFT = "#8B2E3C";
const SHS_INK = "#2A1218";

const SHS_THEME = {
  id: "shs",
  displayName: "SHS",
  documentTitle: "SHS Schooling System",
  favicon: "/shs-favicon.ico",
  colors: {
    bg: "#faf7f8",
    panel: "#ffffff",
    ink: SHS_INK,
    muted: "#6b5a5e",
    line: "rgba(107, 30, 42, 0.2)",
    accent: SHS_MAROON,
    accentSoft: SHS_MAROON_SOFT,
    accentDeep: SHS_MAROON_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(107, 30, 42, 0.14)",
    accentRgb: "107, 30, 42",
    accentSoftRgb: "139, 46, 60",
    gradient: `linear-gradient(135deg, ${SHS_MAROON_DEEP} 0%, ${SHS_MAROON} 55%, ${SHS_MAROON_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #2E0C14 0%, ${SHS_MAROON_DEEP} 100%)`,
  },
  dark: {
    bg: "#161012",
    panel: "#22181a",
    ink: "#f5f0f1",
    muted: "#b8a8ac",
    line: "rgba(139, 46, 60, 0.35)",
    accent: SHS_MAROON_SOFT,
    accentSoft: SHS_MAROON,
    accentDeep: "#b84858",
    input: "#2a2224",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "139, 46, 60",
    accentSoftRgb: "107, 30, 42",
    gradient: `linear-gradient(135deg, ${SHS_MAROON_DEEP} 0%, ${SHS_MAROON} 50%, ${SHS_MAROON_SOFT} 100%)`,
    gradientHover: `linear-gradient(135deg, #2E0C14 0%, ${SHS_MAROON} 100%)`,
  },
};

/** Stanmore Group of Institutions — navy ring + SGI red accent */
const STANMORE_NAVY = "#0C2D6B";
const STANMORE_NAVY_DEEP = "#081F4A";
const STANMORE_NAVY_SOFT = "#1A4A8F";
const STANMORE_RED = "#C41E3A";
const STANMORE_INK = "#0A1E3D";

const STANMORE_THEME = {
  id: "stanmore",
  displayName: "Stanmore",
  documentTitle: "Stanmore Group of Institutions",
  favicon: null, // fall back to mySkool
  colors: {
    bg: "#f5f7fb",
    panel: "#ffffff",
    ink: STANMORE_INK,
    muted: "#5a6b7d",
    line: "rgba(12, 45, 107, 0.18)",
    accent: STANMORE_NAVY,
    accentSoft: STANMORE_NAVY_SOFT,
    accentDeep: STANMORE_NAVY_DEEP,
    input: "#ffffff",
    shadow: "0 16px 40px rgba(12, 45, 107, 0.14)",
    accentRgb: "12, 45, 107",
    accentSoftRgb: "26, 74, 143",
    gradient: `linear-gradient(135deg, ${STANMORE_NAVY_DEEP} 0%, ${STANMORE_NAVY} 55%, ${STANMORE_RED} 100%)`,
    gradientHover: `linear-gradient(135deg, #051530 0%, ${STANMORE_NAVY_DEEP} 100%)`,
  },
  dark: {
    bg: "#0c1420",
    panel: "#152032",
    ink: "#eef3fa",
    muted: "#9bb0c4",
    line: "rgba(26, 74, 143, 0.35)",
    accent: STANMORE_NAVY_SOFT,
    accentSoft: STANMORE_NAVY,
    accentDeep: "#4d7ab8",
    input: "#1a2a3a",
    shadow: "0 20px 50px rgba(0, 0, 0, 0.45)",
    accentRgb: "26, 74, 143",
    accentSoftRgb: "12, 45, 107",
    gradient: `linear-gradient(135deg, ${STANMORE_NAVY_DEEP} 0%, ${STANMORE_NAVY} 50%, ${STANMORE_RED} 100%)`,
    gradientHover: `linear-gradient(135deg, #051530 0%, ${STANMORE_NAVY} 100%)`,
  },
};

/**
 * Registry — add new school themes here.
 */
export const LOGIN_THEMES = {
  myskool: DEFAULT_THEME,
  default: DEFAULT_THEME,
  hpgs: HPGS_THEME,
  "hpgs-connect": {
    ...HPGS_THEME,
    id: "hpgs-connect",
    displayName: "HPGS Connect",
    documentTitle: "HPGS Connect",
    favicon: "/hpgs-logo.png",
  },
  iuss: IUSS_THEME,
  mak: MAK_THEME,
  crestview: CRESTVIEW_THEME,
  elixir: ELIXIR_THEME,
  profectus: PROFECTUS_THEME,
  shs: SHS_THEME,
  stanmore: STANMORE_THEME,
};

/** hostname → theme id */
const HOST_THEME_MAP = {
  "connect.hpgs.pk": "hpgs-connect",
  "erp.iuss.edu.pk": "iuss",
  "iuss.edu.pk": "iuss",
  "portal.makcollegiate.com": "mak",
  "makcollegiate.com": "mak",
  "shsschool.myskool.app": "shs",
};

/** report domain (underscores stripped, lower) → theme id */
const REPORT_THEME_MAP = {
  myskoolhpgs: "hpgs",
  myskooliuss: "iuss",
  myskoolmak: "mak",
  myskoolmakcollegiate: "mak",
  myskoolmakjauhar: "mak",
  myskoolcrestviewacademy: "crestview",
  myskoolcrestview: "crestview",
  myskoolelixir: "elixir",
  myskoolprofectus: "profectus",
  shsschool: "shs",
  myskoolshs: "shs",
  myskoolshsschool: "shs",
  myskoolstanmore: "stanmore",
};

/** welcome / brand display name (lower) → theme id */
const BRAND_THEME_MAP = {
  hpgs: "hpgs",
  "hpgs connect": "hpgs-connect",
  iuss: "iuss",
  "iu school system": "iuss",
  mak: "mak",
  "mak collegiate": "mak",
  "crest view": "crestview",
  crestview: "crestview",
  "crest view academy": "crestview",
  elixir: "elixir",
  "the elixir school": "elixir",
  profectus: "profectus",
  "profectus international": "profectus",
  shs: "shs",
  "shs schooling system": "shs",
  stanmore: "stanmore",
  "stanmore group": "stanmore",
  "stanmore group of institutions": "stanmore",
  sgi: "stanmore",
};

function normalizeKey(value) {
  return String(value || "")
    .replace(/_/g, "")
    .toLowerCase()
    .trim();
}

/** Friendly welcome / tab brand from Campuses/GetClientID report key */
export function brandNameFromReport(report) {
  const key = normalizeKey(report);
  if (!key) return "";

  const known = {
    myskoolhpgs: "HPGS",
    myskooliuss: "IUSS",
    myskoolmak: "MAK Collegiate",
    myskoolmakcollegiate: "MAK Collegiate",
    myskoolmakjauhar: "MAK Collegiate",
    myskoolmakhdoom: "Makhdoom",
    myskoolelixir: "Elixir",
    myskoolyakubov: "Yakubov",
    myskoolprofectus: "Profectus",
    myskoolcrestviewacademy: "Crest View",
    myskoolcrestview: "Crest View",
    myskoolaba: "ABA",
    myskoolksa: "KSA",
    shsschool: "SHS",
    myskoolshs: "SHS",
    myskoolshsschool: "SHS",
    myskoolstanmore: "Stanmore",
    myskool: "mySkool",
  };
  if (known[key]) return known[key];

  if (key.includes("shsschool") || key.includes("myskoolshs")) return "SHS";
  if (key.includes("crestview")) return "Crest View";
  if (key.includes("stanmore")) return "Stanmore";

  if (key.startsWith("myskool") && key.length > 7) {
    const rest = key.slice(7);
    // Exact MAK keys only — never match makhdoom via includes("mak")
    if (
      rest === "mak" ||
      rest === "makcollegiate" ||
      rest === "makjauhar"
    ) {
      return "MAK Collegiate";
    }
    return rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  return "";
}

/**
 * Resolve which login theme to use.
 * @param {{ hostname?: string, report?: string, brandName?: string, themeId?: string }} ctx
 */
export function resolveLoginTheme(ctx = {}) {
  const { hostname = "", report = "", brandName = "", themeId = "" } = ctx;

  if (themeId && LOGIN_THEMES[themeId]) {
    return LOGIN_THEMES[themeId];
  }

  const host = String(hostname || "").toLowerCase();
  if (host === "connect.hpgs.pk" || host.includes("connect.hpgs")) {
    return LOGIN_THEMES["hpgs-connect"];
  }
  if (HOST_THEME_MAP[host] && LOGIN_THEMES[HOST_THEME_MAP[host]]) {
    return LOGIN_THEMES[HOST_THEME_MAP[host]];
  }

  // Partial host matches — only exact school host cues (avoid "mak" matching makhdoom)
  if (host.includes("makcollegiate")) return LOGIN_THEMES.mak;
  if (host.includes("iuss")) return LOGIN_THEMES.iuss;
  if (host.includes("hpgs")) return LOGIN_THEMES.hpgs;
  if (host.includes("shsschool") || host.startsWith("shs.")) return LOGIN_THEMES.shs;
  if (host.includes("crestview")) return LOGIN_THEMES.crestview;
  if (host.includes("elixir")) return LOGIN_THEMES.elixir;
  if (host.includes("profectus")) return LOGIN_THEMES.profectus;
  if (host.includes("stanmore")) return LOGIN_THEMES.stanmore;

  const reportKey = normalizeKey(report);
  if (REPORT_THEME_MAP[reportKey] && LOGIN_THEMES[REPORT_THEME_MAP[reportKey]]) {
    return LOGIN_THEMES[REPORT_THEME_MAP[reportKey]];
  }

  // When a real client report is present, resolve from report only.
  // Prevents stale localStorage brand (e.g. "MAK Collegiate") leaking onto makhdoom etc.
  if (reportKey && reportKey !== "reports") {
    if (
      reportKey === "myskoolmak" ||
      reportKey === "myskoolmakcollegiate" ||
      reportKey === "myskoolmakjauhar" ||
      reportKey.includes("makcollegiate")
    ) {
      return LOGIN_THEMES.mak;
    }
    if (reportKey.includes("iuss")) return LOGIN_THEMES.iuss;
    if (reportKey.includes("hpgs")) return LOGIN_THEMES.hpgs;
    if (reportKey.includes("crestview")) return LOGIN_THEMES.crestview;
    if (reportKey.includes("elixir")) return LOGIN_THEMES.elixir;
    if (reportKey.includes("profectus")) return LOGIN_THEMES.profectus;
    if (
      reportKey.includes("shsschool") ||
      reportKey.includes("myskoolshs")
    ) {
      return LOGIN_THEMES.shs;
    }
    if (reportKey.includes("stanmore")) return LOGIN_THEMES.stanmore;
    if (reportKey.startsWith("myskool")) {
      return LOGIN_THEMES.myskool;
    }
  }

  const brandKey = String(brandName || "")
    .toLowerCase()
    .trim();
  if (BRAND_THEME_MAP[brandKey] && LOGIN_THEMES[BRAND_THEME_MAP[brandKey]]) {
    return LOGIN_THEMES[BRAND_THEME_MAP[brandKey]];
  }

  // Precise fuzzy matches only (never bare "mak" — clashes with makhdoom)
  if (
    brandKey === "mak collegiate" ||
    brandKey.includes("mak collegiate")
  ) {
    return LOGIN_THEMES.mak;
  }
  if (brandKey.includes("iuss")) {
    return LOGIN_THEMES.iuss;
  }
  if (brandKey.includes("hpgs connect")) {
    return LOGIN_THEMES["hpgs-connect"];
  }
  if (brandKey.includes("hpgs")) {
    return LOGIN_THEMES.hpgs;
  }
  if (brandKey.includes("crest view") || brandKey.includes("crestview")) {
    return LOGIN_THEMES.crestview;
  }
  if (brandKey.includes("elixir")) {
    return LOGIN_THEMES.elixir;
  }
  if (brandKey.includes("profectus")) {
    return LOGIN_THEMES.profectus;
  }
  if (brandKey === "shs" || brandKey.includes("shs schooling")) {
    return LOGIN_THEMES.shs;
  }
  if (brandKey.includes("stanmore") || brandKey === "sgi") {
    return LOGIN_THEMES.stanmore;
  }

  return LOGIN_THEMES.myskool;
}

/** CSS custom properties object for :style binding */
export function loginThemeToCssVars(theme, { dark = false } = {}) {
  const c = dark && theme.dark ? theme.dark : theme.colors;
  return {
    "--ms-bg": c.bg,
    "--ms-panel": c.panel,
    "--ms-ink": c.ink,
    "--ms-muted": c.muted,
    "--ms-line": c.line,
    "--ms-accent": c.accent,
    "--ms-accent-soft": c.accentSoft,
    "--ms-accent-deep": c.accentDeep,
    "--ms-input": c.input,
    "--ms-shadow": c.shadow,
    "--ms-accent-rgb": c.accentRgb,
    "--ms-accent-soft-rgb": c.accentSoftRgb,
    "--ms-gradient": c.accent,
    "--ms-gradient-hover": c.accentDeep,
    "--ms-brand": c.accent,
  };
}

/** School favicon, or login-cover logo, or mySkool default */
function isUsableLogoUrl(url) {
  const u = String(url || "").trim();
  if (!u || u === "-" || u === "null" || u === "undefined") return false;
  return /^(https?:|data:|blob:|\/|data)/i.test(u) || u.startsWith("http");
}

export function resolveThemeFavicon(theme, coverUrl) {
  let cover = coverUrl;
  if (!isUsableLogoUrl(cover)) {
    try {
      cover = localStorage.getItem("myskool-login-cover") || "";
    } catch (e) {
      cover = "";
    }
  }
  // Prefer the same logo shown on the login page when theme has no dedicated favicon
  if ((!theme || !theme.favicon) && isUsableLogoUrl(cover)) {
    return cover;
  }
  if (theme && theme.favicon) return theme.favicon;
  if (isUsableLogoUrl(cover)) return cover;
  return DEFAULT_FAVICON;
}

/** Persist splash loader accent for next cold load (public/index.html reads these) */
export function persistSplashTheme(theme) {
  if (!theme || !theme.colors) return;
  try {
    localStorage.setItem("myskool-splash-theme-id", theme.id || "myskool");
    localStorage.setItem("myskool-splash-accent", theme.colors.accent);
    localStorage.setItem("myskool-splash-accent-soft", theme.colors.accentSoft);
    localStorage.setItem("myskool-splash-accent-rgb", theme.colors.accentRgb);
    if (theme.documentTitle) {
      localStorage.setItem("myskool-document-title", theme.documentTitle);
    }
    localStorage.setItem("myskool-favicon", resolveThemeFavicon(theme));
    // Only stamp school welcome brand for non-default themes (don't wipe Makhdoom → mySkool)
    if (theme.displayName && theme.id && theme.id !== "myskool") {
      localStorage.setItem("myskool-welcome-brand", theme.displayName);
    }
  } catch (e) {
    /* ignore */
  }
}

/** Update browser tab title + favicon to match brand */
export function applyBrandDocumentChrome(theme, coverUrl) {
  if (!theme || typeof document === "undefined") return;

  if (theme.documentTitle) {
    document.title = theme.documentTitle;
  }

  const href = resolveThemeFavicon(theme, coverUrl);
  let link =
    document.querySelector("link[rel='icon']") ||
    document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "icon");
    document.head.appendChild(link);
  }
  const isIco = /\.ico(\?|$)/i.test(href);
  link.setAttribute("type", isIco ? "image/x-icon" : "image/png");
  // Bust cache when swapping school logos
  link.setAttribute("href", href);

  try {
    localStorage.setItem("myskool-favicon", href);
  } catch (e) {
    /* ignore */
  }

  persistSplashTheme(theme);
}

export default LOGIN_THEMES;
